import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ModalFrame } from '../ui/modal';
import { IconClose, IconHistory } from '../ui/icons';
import { phraseFor } from './activity';
import { formatWhen } from './time';

// The audit trail: who created/edited/archived a change, who touched a project.
// Sentence-style feed (who + phrase + target · detail), newest first. Frontend
// phase reads from local `activity` state passed in by the page.
export default function WebChangeActivityModal({ activity, projects, onClose }) {
  const [projectFilter, setProjectFilter] = useState('all');
  const projName = useMemo(() => Object.fromEntries((projects || []).map(p => [p.id, p.name])), [projects]);

  const rows = useMemo(() => {
    const list = projectFilter === 'all' ? activity : activity.filter(a => a.project_id === projectFilter);
    return [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [activity, projectFilter]);

  return (
    <ModalFrame onClose={onClose}>
      <motion.div
        role="dialog"
        initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.18 }}
        style={{ width: 'min(680px, calc(100vw - 48px))', maxHeight: 'calc(100vh - 96px)', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(15,20,25,0.22), 0 2px 6px rgba(15,20,25,0.08)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subdued)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--surface-inverted)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconHistory size={15} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-emphasized)' }}>Activity Log</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <select className="form-select" style={{ width: 'auto', fontSize: 12, padding: '5px 8px' }} value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
              <option value="all">All projects</option>
              {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subdued)', padding: 6, display: 'flex' }}>
              <IconClose size={16} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: 'var(--surface-elevated)' }}>
          {rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>No activity yet</div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--border-subdued)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Who', 'What happened', 'When'].map(h => (
                      <th key={h} style={{ textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-subdued)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', padding: '9px 16px', borderBottom: '1px solid var(--border-subdued)', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(e => {
                    const target = e.target || projName[e.project_id] || '';
                    return (
                      <tr key={e.id}>
                        <td style={{ padding: '9px 16px', borderBottom: '1px solid var(--border-subdued)', fontWeight: 600, color: 'var(--text-emphasized)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{e.actor_name || 'Someone'}</td>
                        <td style={{ padding: '9px 16px', borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-default)' }}>
                          {phraseFor(e.action_type)} <strong style={{ color: 'var(--text-emphasized)' }}>{target}</strong>
                          {e.detail && <span style={{ color: 'var(--text-subdued)' }}> — {e.detail}</span>}
                        </td>
                        <td style={{ padding: '9px 16px', borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-subdued)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{formatWhen(e.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </ModalFrame>
  );
}
